export type TechnologyBranch =
  | 'industry'
  | 'aviation'
  | 'electronics'
  | 'rocketry'
  | 'spaceflight'

export type TechnologyId =
  | 'industrial-standardization'
  | 'vacuum-electronics'
  | 'jet-propulsion'
  | 'liquid-rocketry'
  | 'radar-tracking'
  | 'inertial-guidance'
  | 'multistage-rockets'
  | 'orbital-flight'
  | 'life-support'
  | 'crewed-spacecraft'

export interface TechnologyDefinition {
  id: TechnologyId
  name: string
  branch: TechnologyBranch
  tier: 1 | 2 | 3 | 4
  researchCost: number
  prerequisites: TechnologyId[]
  description: string
  unlockHint: string
}

export const TECHNOLOGY_DEFINITIONS: TechnologyDefinition[] = [
  {
    id: 'industrial-standardization',
    name: '工业标准化',
    branch: 'industry',
    tier: 1,
    researchCost: 16,
    prerequisites: [],
    description: '建立统一零部件、公差与批量制造规范。',
    unlockHint: '为喷气推进与液体火箭提供工业基础。',
  },
  {
    id: 'vacuum-electronics',
    name: '真空电子学',
    branch: 'electronics',
    tier: 1,
    researchCost: 16,
    prerequisites: [],
    description: '发展可靠的早期电子设备、通信与控制元件。',
    unlockHint: '为雷达测控和生命保障自动控制奠定基础。',
  },
  {
    id: 'jet-propulsion',
    name: '喷气推进',
    branch: 'aviation',
    tier: 2,
    researchCost: 24,
    prerequisites: ['industrial-standardization'],
    description: '建立现代喷气发动机与高速航空动力基础。',
    unlockHint: '后续军事航空科技的核心前置。',
  },
  {
    id: 'liquid-rocketry',
    name: '液体火箭',
    branch: 'rocketry',
    tier: 2,
    researchCost: 24,
    prerequisites: ['industrial-standardization'],
    description: '掌握可控液体推进、发动机试验与基础火箭结构。',
    unlockHint: '开启多级火箭和轨道飞行路线。',
  },
  {
    id: 'radar-tracking',
    name: '雷达与测控',
    branch: 'electronics',
    tier: 2,
    researchCost: 24,
    prerequisites: ['vacuum-electronics'],
    description: '建立雷达探测、遥测和远距离跟踪网络。',
    unlockHint: '支撑制导系统与早期轨道任务。',
  },
  {
    id: 'inertial-guidance',
    name: '惯性制导',
    branch: 'electronics',
    tier: 3,
    researchCost: 32,
    prerequisites: ['radar-tracking'],
    description: '利用惯性器件实现自主姿态和飞行路径控制。',
    unlockHint: '载人飞船和先进导弹的重要前置。',
  },
  {
    id: 'multistage-rockets',
    name: '多级火箭',
    branch: 'rocketry',
    tier: 3,
    researchCost: 32,
    prerequisites: ['liquid-rocketry'],
    description: '通过级间分离显著提高有效载荷与飞行速度。',
    unlockHint: '未来卫星和深空任务的运载基础。',
  },
  {
    id: 'orbital-flight',
    name: '轨道飞行理论',
    branch: 'spaceflight',
    tier: 3,
    researchCost: 32,
    prerequisites: ['liquid-rocketry', 'radar-tracking'],
    description: '掌握轨道计算、入轨窗口和地面测控协同。',
    unlockHint: '未来卫星与载人轨道任务的直接前置。',
  },
  {
    id: 'life-support',
    name: '生命保障',
    branch: 'spaceflight',
    tier: 3,
    researchCost: 32,
    prerequisites: ['industrial-standardization', 'vacuum-electronics'],
    description: '建立密闭座舱环境控制、供氧与基础生存保障。',
    unlockHint: '载人航天器不可缺少的系统工程能力。',
  },
  {
    id: 'crewed-spacecraft',
    name: '载人飞船',
    branch: 'spaceflight',
    tier: 4,
    researchCost: 48,
    prerequisites: ['orbital-flight', 'life-support', 'inertial-guidance'],
    description: '整合制导、生命保障、结构和返回系统形成载人飞船。',
    unlockHint: 'S06-B 载人轨道任务的关键科技。',
  },
]

export function getTechnologyDefinition(
  technologyId: TechnologyId,
): TechnologyDefinition {
  const definition = TECHNOLOGY_DEFINITIONS.find(
    (technology) => technology.id === technologyId,
  )

  if (!definition) {
    throw new Error(`Unknown technology: ${technologyId}`)
  }

  return definition
}

export function getTechnologyBranchLabel(
  branch: TechnologyBranch,
): string {
  const labels: Record<TechnologyBranch, string> = {
    industry: '工业',
    aviation: '航空',
    electronics: '电子与测控',
    rocketry: '火箭',
    spaceflight: '载人航天',
  }

  return labels[branch]
}

export function isTechnologyId(value: unknown): value is TechnologyId {
  return TECHNOLOGY_DEFINITIONS.some(
    (technology) => technology.id === value,
  )
}
